import { useState } from 'react';
import type { Plan, CurrentSubscription } from './types';
import type { SiteContentSnapshot } from './downgradeCompliance';

export interface DowngradeResourceWizardPayload {
  mode: 'free' | 'paid';
  planId?: string;
  planDisplayName: string;
  maxSites: number;
  maxPagesPerSite: number;
  snapshots: SiteContentSnapshot[];
}

export interface PricingState {
  plans: Plan[];
  setPlans: React.Dispatch<React.SetStateAction<Plan[]>>;
  currentSubscription: CurrentSubscription | null;
  setCurrentSubscription: React.Dispatch<React.SetStateAction<CurrentSubscription | null>>;
  billingPeriod: 'monthly' | 'yearly';
  setBillingPeriod: React.Dispatch<React.SetStateAction<'monthly' | 'yearly'>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  upgrading: string | null;
  setUpgrading: React.Dispatch<React.SetStateAction<string | null>>;
  showDowngradeModal: boolean;
  setShowDowngradeModal: React.Dispatch<React.SetStateAction<boolean>>;
  downgrading: boolean;
  setDowngrading: React.Dispatch<React.SetStateAction<boolean>>;
  downgradeError: string | null;
  setDowngradeError: React.Dispatch<React.SetStateAction<string | null>>;
  checkoutError: string | null;
  setCheckoutError: React.Dispatch<React.SetStateAction<string | null>>;
  showErrorToast: boolean;
  setShowErrorToast: React.Dispatch<React.SetStateAction<boolean>>;
  planChangeModal: { kind: 'upgrade' | 'paid-downgrade'; planId: string } | null;
  setPlanChangeModal: React.Dispatch<
    React.SetStateAction<{ kind: 'upgrade' | 'paid-downgrade'; planId: string } | null>
  >;
  /** When set, full-screen wizard to delete sites/pages before subscription change. */
  downgradeResourceWizard: DowngradeResourceWizardPayload | null;
  setDowngradeResourceWizard: React.Dispatch<
    React.SetStateAction<DowngradeResourceWizardPayload | null>
  >;
  downgradePreviewLoading: boolean;
  setDowngradePreviewLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onboardingStatus: {
    completed: boolean;
    hasPaymentMethod: boolean;
    requiresPaymentSetup: boolean;
  } | null;
  setOnboardingStatus: React.Dispatch<
    React.SetStateAction<{
      completed: boolean;
      hasPaymentMethod: boolean;
      requiresPaymentSetup: boolean;
    } | null>
  >;
  plansLoadedFromApi: boolean;
  setPlansLoadedFromApi: React.Dispatch<React.SetStateAction<boolean>>;
}

export const usePricingState = (): PricingState => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [downgrading, setDowngrading] = useState(false);
  const [downgradeError, setDowngradeError] = useState<string | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [planChangeModal, setPlanChangeModal] = useState<{
    kind: 'upgrade' | 'paid-downgrade';
    planId: string;
  } | null>(null);
  const [downgradeResourceWizard, setDowngradeResourceWizard] =
    useState<DowngradeResourceWizardPayload | null>(null);
  const [downgradePreviewLoading, setDowngradePreviewLoading] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState<{
    completed: boolean;
    hasPaymentMethod: boolean;
    requiresPaymentSetup: boolean;
  } | null>(null);
  const [plansLoadedFromApi, setPlansLoadedFromApi] = useState(false);

  return {
    plans,
    setPlans,
    currentSubscription,
    setCurrentSubscription,
    billingPeriod,
    setBillingPeriod,
    loading,
    setLoading,
    upgrading,
    setUpgrading,
    showDowngradeModal,
    setShowDowngradeModal,
    downgrading,
    setDowngrading,
    downgradeError,
    setDowngradeError,
    checkoutError,
    setCheckoutError,
    showErrorToast,
    setShowErrorToast,
    planChangeModal,
    setPlanChangeModal,
    downgradeResourceWizard,
    setDowngradeResourceWizard,
    downgradePreviewLoading,
    setDowngradePreviewLoading,
    onboardingStatus,
    setOnboardingStatus,
    plansLoadedFromApi,
    setPlansLoadedFromApi,
  };
};
